/**
 * Phase 49 — post-login return paths.
 *
 * The OAuth consent screen (`/developer/oauth/authorize?client_id=…`)
 * is the first dashboard route where landing on `/` after login is a
 * broken flow rather than a mild annoyance: the query string IS the
 * authorization request, and losing it strands the third-party app.
 * ProtectedRoute now forwards where you were headed, and the auth
 * pages send you back there.
 *
 * Only SAME-ORIGIN, path-shaped values survive. An attacker who can
 * put `?next=` on a login link would otherwise have a phishing
 * redirect that inherits our domain's credibility — so anything that
 * could resolve to another origin is dropped in favour of `/`.
 */

export const DEFAULT_NEXT_PATH = '/';

export function safeNextPath(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_NEXT_PATH;

  const value = raw.trim();
  // Must be a rooted path. Rejects `https://evil.test`, `//evil.test`
  // (protocol-relative), and `javascript:` / `data:` schemes.
  if (!value.startsWith('/') || value.startsWith('//')) {
    return DEFAULT_NEXT_PATH;
  }
  // Backslashes are normalized to forward slashes by some browsers,
  // which turns `/\evil.test` into a protocol-relative URL.
  if (value.includes('\\')) return DEFAULT_NEXT_PATH;
  // Control characters can be used to smuggle a scheme past the checks.
  // eslint-disable-next-line no-control-regex -- deliberately matching control chars
  if (/[ -]/.test(value)) return DEFAULT_NEXT_PATH;

  return value;
}

/** Build the `?next=` suffix for a login redirect, or '' at the root. */
export function nextQuery(pathname: string, search = ''): string {
  const target = `${pathname}${search}`;
  if (target === DEFAULT_NEXT_PATH) return '';
  return `?next=${encodeURIComponent(target)}`;
}
